import React, { ReactNode, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import TerminalIcon from '@mui/icons-material/Terminal';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import DashboardIcon from '@mui/icons-material/Dashboard';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import GroupIcon from '@mui/icons-material/Group';
import LogoutIcon from '@mui/icons-material/Logout';

interface Props {
  children: ReactNode;
}

type NavTab = 'comparison' | 'scrappers' | 'items' | 'users' | 'dashboard';

const NAV_ITEMS: { id: NavTab; label: string; icon: React.ReactNode }[] = [
  { id: 'dashboard', label: 'Dashboard', icon: <DashboardIcon /> },
  { id: 'comparison', label: 'Comparison', icon: <SwapHorizIcon /> },
  { id: 'scrappers', label: 'Scrappers', icon: <CloudSyncIcon /> },
  { id: 'items', label: 'Inventory', icon: <Inventory2Icon /> },
  { id: 'users', label: 'Users', icon: <GroupIcon /> },
];

export const Layout = ({ children }: Props) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const SIDEBAR_COLLAPSED_KEY = 'scrapper_sidebar_collapsed';
  const [isDesktopCollapsed, setIsDesktopCollapsed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    try {
      return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === '1';
    } catch {
      return false;
    }
  });

  const toggleDesktopCollapsed = () => {
    setIsDesktopCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? '1' : '0');
      } catch {
        // localStorage may be unavailable (private mode, quota); state still updates in-memory.
      }
      return next;
    });
  };

  const activeTab = NAV_ITEMS.find((item) =>
    location.pathname.startsWith('/' + item.id)
  )?.id || 'dashboard';

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  return (
    <div className="flex min-h-screen bg-background text-on-surface font-body">
      {/* Mobile overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 lg:hidden backdrop-blur-sm transition-opacity"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <nav
        className={`bg-sidebar-bg font-body text-body-sm h-screen ${isDesktopCollapsed ? 'w-[68px]' : 'w-[260px]'} fixed left-0 top-0 flex flex-col py-4 z-50 transition-all duration-200 ${
          isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
        }`}
      >
        {/* Brand */}
        <div className="px-6 py-4 flex items-center space-x-3 mb-4">
          <div className="w-8 h-8 rounded-lg bg-primary-container flex items-center justify-center shrink-0">
            <TerminalIcon className="text-on-primary-container text-[20px]" />
          </div>
          <div className={`${isDesktopCollapsed ? 'lg:hidden' : ''}`}>
            <div className="font-headline-sm text-white font-bold tracking-tight uppercase">
              Scraper Panel
            </div>
            <div className="font-label-caps text-label-caps text-secondary-fixed-dim">
              Admin Terminal
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex-1 overflow-y-auto px-3 space-y-1">
          {NAV_ITEMS.map((item) => {
            const isActive = activeTab === item.id;
            return (
              <button
                key={item.id}
                onClick={() => {
                  navigate('/' + item.id);
                  setIsMobileOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all group ${isDesktopCollapsed ? 'lg:justify-center' : ''} ${
                  isActive
                    ? 'bg-primary text-white font-medium'
                    : 'text-secondary-fixed-dim hover:bg-secondary-fixed/10 hover:text-white'
                }`}
                title={item.label}
              >
                <span className={`text-[20px]${isActive ? ' icon-fill' : ''}`}>{item.icon}</span>
                <span className={`text-sm ${isDesktopCollapsed ? 'lg:hidden' : ''}`}>{item.label}</span>
              </button>
            );
          })}
        </div>

        {/* Bottom section — logout only */}
        <div className="px-3 py-3 mt-auto border-t border-white/10">
          <button
            onClick={handleLogout}
            className={`w-full flex items-center space-x-3 px-3 py-2 rounded text-on-surface-variant/70 text-white hover:text-danger hover:bg-white/5 transition-colors ${isDesktopCollapsed ? 'lg:justify-center' : ''}`}
            title="Logout"
          >
            <LogoutIcon className="text-[20px]" />
            <span className={`text-sm ${isDesktopCollapsed ? 'lg:hidden' : ''}`}>Logout</span>
          </button>
        </div>
      </nav>

      {/* Main content */}
      <div className={`flex-1 ml-0 ${isDesktopCollapsed ? 'lg:ml-[68px]' : 'lg:ml-[260px]'} flex flex-col min-h-screen min-w-0`}>
        {/* Top bar */}
        <header className="h-16 bg-surface/80 backdrop-blur-xl border-b border-border-subtle flex items-center justify-between px-4 lg:px-6 sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <button
              className="lg:hidden p-2 rounded hover:bg-surface-low transition-colors"
              onClick={() => setIsMobileOpen(true)}
            >
              <MenuIcon className="text-on-surface-variant" />
            </button>

            <button
              type="button"
              className="hidden lg:flex p-2 rounded hover:bg-surface-low transition-colors"
              onClick={toggleDesktopCollapsed}
              aria-label="Toggle sidebar"
            >
              <MenuOpenIcon className={`text-on-surface-variant transition-transform duration-200 ${isDesktopCollapsed ? 'rotate-180' : ''}`} />
            </button>

            <div className="font-headline-sm text-on-surface font-semibold hidden lg:block">
              {NAV_ITEMS.find((i) => i.id === activeTab)?.label || 'Dashboard'}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <span className="text-xs text-on-surface-variant hidden sm:block">
              {new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
            </span>
            <div className="w-8 h-8 rounded-full bg-primary-container flex items-center justify-center">
              <span className="text-on-primary text-xs font-bold">
                {user?.username?.charAt(0).toUpperCase() || 'A'}
              </span>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1">
          {children}
        </main>

        {/* Footer */}
        <footer className="h-8 border-t border-border-subtle bg-surface-lowest flex items-center px-4 text-[10px] text-on-surface-variant">
          <span>Scraper Panel — Admin Terminal</span>
        </footer>
      </div>
    </div>
  );
};
